import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useOnMount } from 'shared/web';
import { getCategoryColor, type CategoryColorScheme } from '../constants/categoryColors';
import type { EagColumn } from '../types/EagColumn';
import type { EagColumnGroup, EagColumnOrGroup } from '../types/EagColumnGroup';
import type { FilterState } from '../types/FilterState';
import { isEagColumnGroup } from '../utils/isEagColumnGroup';
export type CategoryItem = {
  id: string;
  label: string;
  color: CategoryColorScheme;
  activeCount: number;
  /** Lowercase headerNames of all filterable children — used for cross-category search. */
  childLabels: string[];
};
type UseFilterCategorySelectionParams = {
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  searchTerm: string;
};
type UseFilterCategorySelectionResult = {
  visibleItems: CategoryItem[];
  categoryItems: CategoryItem[];
  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string) => void;
};

/**
 * Returns the number of active filters within a set of columns.
 *
 * A filter value is "active" when it is not nil, not `false`, and (for arrays)
 * not empty — matching the unwrapFilterValues logic so badge counts are consistent.
 */
export const countActiveFilters = (columns: EagColumn[], filterState: FilterState): number => {
  let count = 0;
  for (const col of columns) {
    if (!col.filter) {
      continue;
    }
    const raw = filterState[col.key];
    if (raw == null) {
      continue;
    }
    const value = typeof raw === 'object' && raw !== null && 'value' in (raw as Record<string, unknown>) ? (raw as Record<string, unknown>).value : raw;
    if (value == null || value === false) {
      continue;
    }
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    count++;
  }
  return count;
};

/**
 * Builds a flat list of internal category items (with childLabels for search)
 * from the columnConfig, merging repeated groupIds.
 *
 * Exported so tests can verify the build logic independently.
 */
export const buildCategoryItems = (columnConfig: EagColumnOrGroup[], filterState: FilterState): CategoryItem[] => {
  const itemMap = new Map<string, CategoryItem>();
  const standaloneColumns: EagColumn[] = [];
  const childrenMap = new Map<string, EagColumn[]>();
  for (const item of columnConfig) {
    if (isEagColumnGroup(item)) {
      const group = item as EagColumnGroup;
      const filterableChildren = group.children.filter((col): col is EagColumn => col != null && !!col.filter);
      const childLabels = filterableChildren.map(col => col.headerName.toLowerCase());
      const existing = itemMap.get(group.groupId);
      if (existing) {
        // Merge children from repeated groupId instances
        existing.childLabels.push(...childLabels);
        const allChildren = childrenMap.get(group.groupId) ?? [];
        allChildren.push(...filterableChildren);
        childrenMap.set(group.groupId, allChildren);
        existing.activeCount = countActiveFilters(allChildren, filterState);
      } else {
        childrenMap.set(group.groupId, [...filterableChildren]);
        itemMap.set(group.groupId, {
          id: group.groupId,
          label: group.headerName,
          color: getCategoryColor(group.groupId),
          activeCount: countActiveFilters(filterableChildren, filterState),
          childLabels
        });
      }
    } else {
      const col = item as EagColumn;
      if (col.filter) {
        standaloneColumns.push(col);
      }
    }
  }
  const items = Array.from(itemMap.values());
  if (standaloneColumns.length > 0) {
    items.push({
      id: '__standalone__',
      label: 'Other',
      color: 'neutral',
      activeCount: countActiveFilters(standaloneColumns, filterState),
      childLabels: standaloneColumns.map(col => col.headerName.toLowerCase())
    });
  }
  return items;
};

/**
 * Manages filter category selection state including:
 * - Building the full category item list from columnConfig + filterState
 * - Filtering visible items by searchTerm (category label or child column labels)
 * - Auto-selecting the first available category on mount
 * - Auto-correcting selection when searchTerm changes and current selection
 *   is no longer in the visible set
 */
export function useFilterCategorySelection(params) {
  const $ = _c(4);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const allItems = buildCategoryItems(params.columnConfig, params.filterState);
  const lowerSearch = params.searchTerm.toLowerCase();
  const visibleItems = lowerSearch ? allItems.filter(item => item.label.toLowerCase().includes(lowerSearch) || item.childLabels.some(label => label.includes(lowerSearch))) : allItems;
  useOnMount(() => {
    if (visibleItems.length > 0) {
      setSelectedCategoryId(visibleItems[0].id);
    }
  });
  const effectiveSelectedCategoryId = selectedCategoryId !== null && visibleItems.some(item_0 => item_0.id === selectedCategoryId) ? selectedCategoryId : visibleItems[0]?.id ?? null;
  let t0;
  if ($[0] !== allItems || $[1] !== effectiveSelectedCategoryId || $[2] !== visibleItems) {
    t0 = {
      visibleItems,
      categoryItems: allItems,
      selectedCategoryId: effectiveSelectedCategoryId,
      setSelectedCategoryId
    };
    $[0] = allItems;
    $[1] = effectiveSelectedCategoryId;
    $[2] = visibleItems;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}